import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { MembershipRole, MembershipStatus, SettableMemberStatus } from "@shyne/shared/api";
import type { TeamMember } from "@/repositories/team-members.repository.ts";
import { ReadAllResponse } from "@/interfaces";
import { listTeamMembers, sendTeamMemberInvite } from "./teammembers.api";
import { InvitePayload } from "./teammembers.entity";
import { changeRoleAndStatus } from "../teammembers-detail/team-member-detail.api";

interface TeamMembersState {
  list: TeamMember[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: {
    role: MembershipRole | "";
    status: MembershipStatus | "";
    sortKey: string;
    sortOrder: "asc" | "desc";
  };
  pagination: {
    page: number;
    pageSize: number;
  };
  editingModalOpen: boolean;
  editingMember: TeamMember | null;
}

export const MEMBER_SORT_KEY = "name";

const initialState: TeamMembersState = {
  list: [],
  loading: true,
  error: null,
  total: 0,
  filters: {
    role: "",
    status: "",
    sortKey: MEMBER_SORT_KEY,
    sortOrder: "asc",
  },
  pagination: {
    page: 1,
    pageSize: 10,
  },
  editingModalOpen: false,
  editingMember: null,
};

export const fetchTeamMembers = createAsyncThunk("teamMembers/fetch", async (_, { getState }) => {
  const state = getState() as { teamMembers: TeamMembersState };
  const { page, pageSize } = state.teamMembers.pagination;
  const { role, status, sortOrder } = state.teamMembers.filters;

  return await listTeamMembers(page, pageSize, { role: role || undefined, status: status || undefined, order: sortOrder });
});

export const inviteTeamMember = createAsyncThunk("teamMembers/invite", async (payload: InvitePayload) => {
  return await sendTeamMemberInvite(payload);
});

export const sendTeamMemberUpdate = createAsyncThunk(
  "teamMembers/update",
  async ({ member, role, status }: { member: TeamMember; role: MembershipRole; status: SettableMemberStatus | undefined }) => {
    return await changeRoleAndStatus(member, role, status);
  }
);

const slice = createSlice({
  name: "teamMembers",
  initialState,
  reducers: {
    setRole(state, action: PayloadAction<MembershipRole | "">) {
      state.filters.role = action.payload;
    },
    setStatus(state, action: PayloadAction<MembershipStatus | "">) {
      state.filters.status = action.payload;
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    },
    setSortKey(state, { payload }: PayloadAction<string>) {
      if (state.filters.sortKey === payload) {
        state.filters.sortOrder = state.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        state.filters.sortKey = payload;
        state.filters.sortOrder = "asc";
      }
    },
    setEditingMember(state, { payload }: PayloadAction<TeamMember | null>) {
      state.editingMember = payload;
    },
    setEditModalOpen(state, { payload }: PayloadAction<boolean>) {
      state.editingModalOpen = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action: PayloadAction<ReadAllResponse<TeamMember>>) => {
        state.loading = false;
        state.list = action.payload.data;
        state.total = action.payload.count ?? 0;
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.list = [];
        state.total = 0;
        state.error = action.error.message || "Failed to fetch team members.";
      })
      .addCase(inviteTeamMember.pending, (state) => {
        state.loading = true;
      })
      .addCase(inviteTeamMember.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(inviteTeamMember.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setRole, setStatus, setPage, setPageSize, setSortKey, setEditingMember, setEditModalOpen } = slice.actions;
export default slice.reducer;
